// src/pages/InventoryPage.js

/**
 * InventoryPage – displays a list of uploaded documents for the current user
 * in a searchable, selectable table with CSV download and delete functionality.
 *
 * Features:
 * - Fetches and displays all uploads for the logged‑in user via `fetchUploads`
 *   (useEffect).
 * - Progressive search filtering via `handleSearchChange`.
 * - Row selection logic:
 *     • `handleCheckboxChange` toggles individual file checkboxes.
 *     • `handleSelectAllFiltered` selects/deselects all visible rows.
 * - Row click loading via `handleRowClick`:
 *     • Fetches JSON for a document.
 *     • Updates `DocumentContext` with `setSelectedDocument` and filename.
 *     • Highlights the active row.
 * - CSV download per file via `downloadCSVForFile`.
 * - Bulk deletion of selected files via `handleDeleteSelected`:
 *     • Calls `/api/delete-uploads`.
 *     • Refetches inventory on success.
 * - Date formatting helper `formatDate` for display of upload timestamps.
 *
 * Contexts:
 * - AuthContext: `currentUser.uid` for userId.
 * - DocumentContext: `setSelectedDocument`, `setDocumentFilename`.
 *
 * Author: Galo Alonso Peralta Espinoza
 * Date: 04-17-2025
 */

import React, { useState, useEffect, useContext, useMemo } from 'react'

// Utils
import {
  downloadFiles
} from '../utils/downloadHelpers'

// Context
import { AuthContext } from '../context/AuthContext'
import { DocumentContext } from '../context/DocumentContext'

// Components
import Navbar from './components/Navbar'
import PaywallModal from './components/PaywallModal'

// Material UI
import { Typography, Menu, MenuItem, Tooltip, Select } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import IconButton from '@mui/material/IconButton'

// Material UI Icons
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import HelpIcon from '@mui/icons-material/Help'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

// Authentication helpers
import { authenticatedFetch, handleAuthError } from '../utils/authHelpers'

// Local CSS
import styles from './InventoryPage.module.css'

function InventoryPage({ onSelectDocument }) {

  // State Variables

  // Read currentUser from AuthContext
  const { currentUser } = useContext(AuthContext)
  const userId = currentUser ? currentUser.uid : null

  // Document Context
  const { setSelectedDocument, setDocumentFilename } = useContext(DocumentContext)
  // State for holding the fetched inventory documents.
  const [inventoryData, setInventoryData] = useState([])
  const [error, setError] = useState('')
  // State for the IDs (or unique keys) of selected rows.
  const [selectedIds, setSelectedIds] = useState([])
  // State for the current search term (progressive search filtering).
  const [searchTerm, setSearchTerm] = useState('')
  // State for current row selected
  const [activeRowId, setActiveRowId] = useState(null)

  // State for download menu
  const [anchorEl, setAnchorEl] = useState(null)
  const [menuFileId, setMenuFileId] = useState(null)
  const [anchorBulkEl, setAnchorBulkEl] = useState(null)

  // State for separate file checkbox
  const [combinedDownload, setCombinedDownload] = useState(false)

  // State for select all pages
  const [selectAllPages, setSelectAllPages] = useState(false)

  // Progress bar state for bulk downloads
  const [downloadProgress, setDownloadProgress] = useState(0)
  const [isDownloading, setIsDownloading] = useState(false)

  // Sorting States
  const [sortColumn, setSortColumn] = useState('createdAt')
  const [sortDirection, setSortDirection] = useState('desc')
  
  // New state for filter
  const [filterModel, setFilterModel] = useState('Any')
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(0)
  const ITEMS_PER_PAGE = 50

  // Subscription State
  const [showPaywallModal, setShowPaywallModal] = useState(false)
  
  //** Fetching Inventory per User Logic **//
  useEffect(() => {
    async function fetchUploads() {
      console.log("InventoryPage: fetching uploads");
      try {
        const response = await authenticatedFetch(`/api/get-uploads?status=all`)
        if (!response.ok) {
          throw new Error(`Failed to fetch uploads: ${response.statusText}`)
        }
        const docs = await response.json()
        console.log("InventoryPage: fetched uploads:", docs);
        setInventoryData(docs)
      } catch (err) {
        console.error('Error fetching uploads:', err)
        
        //? Handle different types of errors appropriately
        if (err.message.includes('not authenticated') || 
            err.message.includes('Authentication expired') ||
            err.message.includes('Session expired') ||
            err.message.includes('Authentication failed')) {
          //? Authentication errors - use handleAuthError
          const userMessage = handleAuthError(err, 'fetch uploads');
          setError(userMessage);
          //? Note: authenticatedFetch will handle redirects automatically
        } else if (err.message.includes('Failed to fetch') || 
                   err.message.includes('NetworkError') ||
                   err.message.includes('fetch')) {
          //? Network errors - user-friendly message
          console.error('Network error fetching uploads:', err);
          setError('Network error. Please check your connection.');
        } else {
          //? Other errors (server errors, etc.) - generic message
          console.error('Server error fetching uploads:', err);
          setError('Unable to load uploads. Please try again.');
        }
      }
    }
    fetchUploads()
  }, [])

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, filterModel]);

  //## ======================= SUBSCRIPTION CHECK ======================= ##//
  
  // Check subscription status on page load
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await authenticatedFetch('/api/user/subscription-status');
        
        if (response.ok) {
          const { subscription } = await response.json();
          setShowPaywallModal(subscription.status === 'inactive' || !subscription.status);
        } else {
          // If we can't verify subscription, show modal for safety
          setShowPaywallModal(true);
        }
      } catch (error) {
        console.error('Subscription check failed:', error);
        // Show modal if subscription check fails
        setShowPaywallModal(true);
      }
    };

    checkSubscription();
  }, []);

  //** Date Formatting Logic **//
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const dateObj = new Date(dateString)
    const year = dateObj.getFullYear()
    const month = String(dateObj.getMonth() + 1).padStart(2, '0')
    const day = String(dateObj.getDate()).padStart(2, '0')
    const hours = String(dateObj.getHours()).padStart(2, '0')
    const minutes = String(dateObj.getMinutes()).padStart(2, '0')
    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  // Utility to format file size
  function formatFileSize(size) {
    if (typeof size === 'string') return size; // Already formatted (legacy)
    if (typeof size !== 'number' || isNaN(size)) return 'N/A';
    
    const sizeInMB = size / (1024 * 1024);
    
    if (sizeInMB >= 1024) {
      return `${(sizeInMB / 1024).toFixed(2)} GB`;
    } else {
      return `${sizeInMB.toFixed(2)} MB`;
    }
  }

  //** Progressive Search Logic + Sorting **//
  const filteredData = useMemo(() => {
    
    //? 1) Filter
    const term = searchTerm.toLowerCase()
    let data = inventoryData.filter(file => {
      const name = (file.originalName || file.fileName || '').toLowerCase()
      // Model filter
      if (filterModel === 'shipping-labels' && file.model !== 'shipping-labels') return false;
      if (filterModel === 'safety-datasheets' && file.model !== 'safety-datasheets') return false;
      return name.includes(term)
    })

    //? 2) Sort
    data.sort((a, b) => {

      // Get values
      let aVal = a[sortColumn]
      let bVal = b[sortColumn]

      // Upload Date (date compare)
      if (sortColumn === 'createdAt') {
        const dA = new Date(aVal), dB = new Date(bVal)
        return sortDirection === 'asc' ? dA - dB : dB - dA
      }

      // File Size (number compare)
      if (sortColumn === 'fileSize') {
        const nA = parseFloat(aVal) || 0
        const nB = parseFloat(bVal) || 0
        return sortDirection === 'asc' ? nA - nB : nB - nA
      }

      // Model (string compare)
      if (sortColumn === 'model') {
        const sA = String(aVal || '').toLowerCase()
        const sB = String(bVal || '').toLowerCase()
        if (sA < sB) {
          return sortDirection === 'asc' ? -1 : 1
        } else if (sA > sB) {
          return sortDirection === 'asc' ? 1 : -1
        } else {
          return 0
        }
      }

      //file Name (string compare)
      if (sortColumn === 'originalName') {
        const sA = String(aVal || '').toLowerCase()
        const sB = String(bVal || '').toLowerCase()
        if (sA < sB) {
          return sortDirection === 'asc' ? -1 : 1
        } else if (sA > sB) {
          return sortDirection === 'asc' ? 1 : -1
        } else {
          return 0
        }
      }

      // Default: return 0
      return 0
    })

    //? 3) Pagination
    const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE)
    const startIndex = currentPage * ITEMS_PER_PAGE
    const endIndex = startIndex + ITEMS_PER_PAGE
    const paginatedData = data.slice(startIndex, endIndex)

    //? 4) Return
    return paginatedData
    
  }, [inventoryData, searchTerm, sortColumn, sortDirection, filterModel, currentPage])

  // Calculate total pages for pagination controls
  const totalPages = useMemo(() => {
    const term = searchTerm.toLowerCase()
    let data = inventoryData.filter(file => {
      const name = (file.originalName || file.fileName || '').toLowerCase()
      if (filterModel === 'shipping-labels' && file.model !== 'shipping-labels') return false;
      if (filterModel === 'safety-datasheets' && file.model !== 'safety-datasheets') return false;
      return name.includes(term)
    })
    return Math.ceil(data.length / ITEMS_PER_PAGE)
  }, [inventoryData, searchTerm, filterModel])

  //** Toggle Selection **//
  const handleCheckboxChange = (fileId) => {
    setSelectedIds((prev) => {
      const isCurrentlySelected = prev.includes(fileId)
      
      if (isCurrentlySelected) {
        // Deselecting - remove from selection
        const newSelection = prev.filter((id) => id !== fileId)
        
        // If we're in "Select All Pages" mode and user deselects something,
        // clear the "Select All Pages" flag
        if (selectAllPages) {
          setSelectAllPages(false)
        }
        
        return newSelection
      } else {
        // Selecting - add to selection
        return [...prev, fileId]
      }
    })
  }
  const handleSelectAllFiltered = () => {
    if (selectAllPages) {
      // "Select All Pages" mode
      if (selectedIds.length === inventoryData.length) {
        setSelectedIds([])
        setSelectAllPages(false)
      } else {
        setSelectedIds(inventoryData.map((file) => file.id))
        setSelectAllPages(true)
      }
    } else {
      // Current page mode (existing behavior)
      if (selectedIds.length === filteredData.length) {
        setSelectedIds([])
      } else {
        setSelectedIds(filteredData.map((file) => file.id))
      }
    }
  }
  
  //** Document Selection Logic **//
  const handleRowClick = async (fileData) => {

    if (!fileData.id) return

    try {
      // Fetch Response with authentication using new POST endpoint
      const response = await authenticatedFetch(
        `/api/get-json-file`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ docIds: [fileData.id] })
        }
      )

      // Check if Response
      if (!response.ok) {
        throw new Error(`Failed to fetch JSON: ${response.statusText}`)
      }

      // Actually Fetch Data from Response
      const result = await response.json()
      
      // Check if we got the document data
      if (!result.documents || result.documents.length === 0 || result.documents[0].error) {
        throw new Error(result.documents?.[0]?.error || 'Document not found')
      }

      const jsonData = result.documents[0].data
      
      // Update Global Context
      setSelectedDocument({
        ...jsonData,
        id: fileData.id,
        model: fileData.model,
      });
      setDocumentFilename(fileData.originalName || '')

      // Set Active row
      setActiveRowId(fileData.id)
      
      if (onSelectDocument) {
        onSelectDocument(jsonData)
      }
    } catch (err) {
      console.error('Error loading JSON from path:', err)
      
      //? Handle different types of errors appropriately
      if (err.message.includes('not authenticated') || 
          err.message.includes('Authentication expired') ||
          err.message.includes('Session expired') ||
          err.message.includes('Authentication failed')) {
        //? Authentication errors - use handleAuthError
        const userMessage = handleAuthError(err, 'load document');
        setError(userMessage);
        //? Note: authenticatedFetch will handle redirects automatically
      } else if (err.message.includes('Failed to fetch') || 
                 err.message.includes('NetworkError') ||
                 err.message.includes('fetch')) {
        //? Network errors - user-friendly message
        console.error('Network error loading document:', err);
        setError('Network error. Please check your connection.');
      } else {
        //? Other errors (server errors, etc.) - generic message
        console.error('Server error loading document:', err);
        setError('Unable to load document. Please try again.');
      }
    }
  }

  //** Download Menu Logic **//
  const handleMenuOpen = (e, fileId) => {
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
    setMenuFileId(fileId)
  }
  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuFileId(null)
  }

  const handleBulkMenuOpen = (e) => {
    e.stopPropagation()
    setAnchorBulkEl(e.currentTarget)
  }
  const handleBulkMenuClose = () => {
    setAnchorBulkEl(null)
  }

  //** Deletion Logic **//
  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return
    try {
      const response = await authenticatedFetch(`/api/delete-uploads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds })
      })
      if (!response.ok) {
        throw new Error(`Failed to delete uploads: ${response.statusText}`)
      }

      // After deletion, refetch the inventory.
      const fetchResponse = await authenticatedFetch(`/api/get-uploads?status=all`)
      if (!fetchResponse.ok) {
        throw new Error(
          `Failed to refetch uploads: ${fetchResponse.statusText}`
        )
      }
      const docs = await fetchResponse.json()
      setInventoryData(docs)

      // Clear selected document IDs.
      setSelectedIds([])
    } catch (err) {
      console.error('Error deleting uploads:', err)
      
      //? Handle different types of errors appropriately
      if (err.message.includes('not authenticated') || 
          err.message.includes('Authentication expired') ||
          err.message.includes('Session expired') ||
          err.message.includes('Authentication failed')) {
        //? Authentication errors - use handleAuthError
        const userMessage = handleAuthError(err, 'delete uploads');
        setError(userMessage);
        //? Note: authenticatedFetch will handle redirects automatically
      } else if (err.message.includes('Failed to fetch') || 
                 err.message.includes('NetworkError') ||
                 err.message.includes('fetch')) {
        //? Network errors - user-friendly message
        console.error('Network error deleting uploads:', err);
        setError('Network error. Please check your connection.');
      } else {
        //? Other errors (server errors, etc.) - generic message
        console.error('Server error deleting uploads:', err);
        setError('Unable to delete uploads. Please try again.');
      }
    }
  }

  // Handler for updating the search term.
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
  }

  //** Model Display Logic **//
  const getModelDisplay = (model) => {
    switch(model) {
      case 'shipping-labels':
        return <span className={`${styles.modelBadge} ${styles.shipping}`}>[SL]</span>
      case 'safety-datasheets':
        return <span className={`${styles.modelBadge} ${styles.safety}`}>[SDS]</span>
      default:
        return <span className={`${styles.modelBadge} ${styles.unknown}`}>[?]</span>
    }
  }

  // Helper to determine if any selected file is a shipping label
  const anySelectedIsSL = selectedIds.some(id => {
    const f = inventoryData.find(x => x.id === id)
    return f && f.model === 'shipping-labels';
  });
  // Helper to determine if more than one model is selected
  const selectedModels = Array.from(new Set(selectedIds.map(id => {
    const f = inventoryData.find(x => x.id === id)
    return f && f.model;
  }).filter(Boolean)));
  const multipleModelsSelected = selectedModels.length > 1;

  // Progress tracking wrapper for bulk downloads
  const handleBulkDownload = async (format, csvFormat = null) => {
    if (selectedIds.length === 0) return;

    setIsDownloading(true);
    setDownloadProgress(0);

    let progressInterval;

    try {
      // Simulate progress - slower and more realistic for bulk downloads
      progressInterval = setInterval(() => {
        setDownloadProgress(prev => {
          if (prev >= 85) {
            clearInterval(progressInterval);
            return 85;
          }
          return prev + 2;
        });
      }, 300);

      // Call the actual download function
      await downloadFiles(
        selectedIds,
        format,
        id => {
          const f = inventoryData.find(x => x.id === id)
          return (f.originalName || f.fileName || id).replace(/\.pdf$/i,'')
        },
        id => {
          const f = inventoryData.find(x => x.id === id)
          return f.model
        },
        combinedDownload,
        csvFormat
      );

      // Complete the progress
      if (progressInterval) clearInterval(progressInterval);
      setDownloadProgress(100);
      setTimeout(() => {
        setIsDownloading(false);
        setDownloadProgress(0);
      }, 2000);

    } catch (error) {
      console.error('Download error:', error);
      if (progressInterval) clearInterval(progressInterval);
      setIsDownloading(false);
      setDownloadProgress(0);
    }

    handleBulkMenuClose();
  };

  return (
    <main className={styles.inventoryPage}>

      {/* NAVBAR */}
      <Navbar />

      {/* MAIN CONTENT */}
      <section className={styles.mainContent}>

        <div className={styles.tableContainer}>

          {/* TABLE CONTROLS */}
          <div className={styles.titleAndPagination}>
            <Typography variant="h6" className={styles.sectionTitle}>
              My Files
            </Typography>
            
            {/* Pagination Controls */}
            <div className={styles.paginationControls}>
              <button
                className={styles.pageArrow}
                onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
                disabled={currentPage === 0}
                aria-label="Previous Page"
              >
                <KeyboardArrowLeftIcon />
              </button>
              <span className={styles.pageInfo}>
                Page {currentPage + 1} of {totalPages || 1}
              </span>
              <button
                className={styles.pageArrow}
                onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
                disabled={currentPage >= totalPages - 1}
                aria-label="Next Page"
              >
                <KeyboardArrowRightIcon />
              </button>
            </div>
          </div>
          
          <div className={styles.tableControls}>

            {/* Search Bar and Filter Dropdown */}
            <div className={styles.searchBar} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search files..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
              <Select
                value={filterModel}
                onChange={e => setFilterModel(e.target.value)}
                displayEmpty
                size="small"
                style={{ height: '2rem', fontSize: '1rem', borderRadius: 5 }}
                renderValue={selected => {
                  if (selected === 'Any') return 'Filter by Document Type';
                  if (selected === 'shipping-labels') return '[SL] - Shipping Labels';
                  if (selected === 'safety-datasheets') return '[SDS] - Safety Datasheets';
                  return selected;
                }}
              >
                <MenuItem value="Any">Any</MenuItem>
                <MenuItem value="shipping-labels">[SL] - Shipping Labels</MenuItem>
                <MenuItem value="safety-datasheets">[SDS] - Safety Datasheets</MenuItem>
              </Select>
            </div>

            <div className={styles.iconControls}>
              {/* Selection Counter */}
              {selectedIds.length > 0 && (
                <div className={styles.selectionCounterInline}>
                  <span>
                     {selectedIds.length} selected
                     {selectAllPages && ` across all pages`}
                     {!selectAllPages && ` on this page`}
                     {(filterModel !== 'any' || searchTerm) && ` (filtered)`}
                  </span>
                </div>
              )}

              {/* Select All Pages Button */}
              <div className={styles.separateFileControl}>
                <Tooltip 
                  title="Select all rows across pages" 
                  arrow
                  placement="top"
                >
                  <button
                    className={`${styles.animatedButton} ${selectAllPages ? styles.animatedButtonActive : ''}`}
                    onClick={() => {
                      if (selectAllPages) {
                        // Deselect all
                        setSelectedIds([])
                        setSelectAllPages(false)
                      } else {
                        // Select all pages (respecting current filter)
                        // Apply both document type filter AND search text filter to the full inventoryData
                        const filteredInventoryData = inventoryData.filter(file => {
                          // Apply document type filter
                          if (filterModel === 'shipping-labels' && file.model !== 'shipping-labels') return false;
                          if (filterModel === 'safety-datasheets' && file.model !== 'safety-datasheets') return false;
                          
                          // Apply search text filter
                          if (searchTerm) {
                            const name = (file.originalName || file.fileName || '').toLowerCase();
                            if (!name.includes(searchTerm.toLowerCase())) return false;
                          }
                          
                          return true;
                        });
                        setSelectedIds(filteredInventoryData.map((file) => file.id))
                        setSelectAllPages(true)
                      }
                    }}
                    type="button"
                  >
                    Select All Pages
                  </button>
                </Tooltip>
              </div>
              
              {/* Combined Download Button */}
              <div className={styles.separateFileControl}>
                <Tooltip 
                  title="Combine files into one download output." 
                  arrow
                  placement="top"
                >
                  <button
                    className={`${styles.animatedButton} ${combinedDownload ? styles.animatedButtonActive : ''}`}
                    onClick={() => setCombinedDownload(!combinedDownload)}
                    type="button"
                  >
                    Combined Download
                  </button>
                </Tooltip>
              </div>

              {/* Download Selected */}
              <div className={styles.downloadIconContainer}>
                <Tooltip title="Download Selected" arrow>
                  <IconButton
                    className={styles.downloadIcon}
                    onClick={handleBulkMenuOpen}
                    disabled={selectedIds.length === 0}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
                {isDownloading && (
                  <div className={styles.progressBarContainer}>
                    <div 
                      className={styles.progressBar}
                      style={{ width: `${downloadProgress}%` }}
                    />
                  </div>
                )}
              </div>
              <Menu
                anchorEl={anchorBulkEl}
                open={Boolean(anchorBulkEl)}
                onClose={handleBulkMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top',    horizontal: 'center' }}
              >
                {(combinedDownload && multipleModelsSelected) ? (
                  <>
                    <Tooltip title="Combined downloads does not support multiple document types at once, please combine one document type at a time."><span><MenuItem disabled>Full CSV</MenuItem></span></Tooltip>
                    <Tooltip title="Combined downloads does not support multiple document types at once, please combine one document type at a time."><span><MenuItem disabled>Simple CSV</MenuItem></span></Tooltip>
                    <Tooltip title="Combined downloads does not support multiple document types at once, please combine one document type at a time."><span><MenuItem disabled>JSON</MenuItem></span></Tooltip>
                    <Tooltip title=""><span><MenuItem disabled>PDF</MenuItem></span></Tooltip>
                  </>
                ) : (
                  <>
                    <MenuItem
                      onClick={() => handleBulkDownload('csv', 'full-csv')}
                      disabled={combinedDownload && multipleModelsSelected}
                    >
                      Full CSV
                    </MenuItem>
                    {anySelectedIsSL ? (
                      <Tooltip title="Simple CSV download currently not supported for shipping labels"><span><MenuItem disabled>Simple CSV</MenuItem></span></Tooltip>
                    ) : (
                      <MenuItem
                        onClick={() => handleBulkDownload('csv', 'simple-csv')}
                      >
                        Simple CSV
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => handleBulkDownload('json')}
                      disabled={combinedDownload && multipleModelsSelected}
                    >
                      JSON
                    </MenuItem>
                    <Tooltip title=""><span><MenuItem disabled>PDF</MenuItem></span></Tooltip>
                  </>
                )}
              </Menu>

              {/* Delete Selected */}
              <Tooltip title="Delete Selected" arrow>
                <IconButton
                  className={styles.deleteIcon}
                  onClick={handleDeleteSelected}
                  disabled={selectedIds.length === 0}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* TABLE */}
          <table className={styles.inventoryTable}>

            {/* TABLE HEADERS */}
            <thead>
              <tr>
                <th>
                  <Tooltip title="Select Page" placement="top">
                    <Checkbox
                      indeterminate={
                        !selectAllPages &&
                        selectedIds.length > 0 &&
                        selectedIds.length < filteredData.length
                      }
                      checked={
                        filteredData.length > 0 &&
                        (selectAllPages || selectedIds.length === filteredData.length)
                      }
                      onChange={handleSelectAllFiltered}
                    />
                  </Tooltip>
                </th>
                <th
                  onClick={() => {
                    if (sortColumn === 'fileSize') {
                      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortColumn('fileSize')
                      setSortDirection('asc')
                    }
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  File Size{sortColumn==='fileSize' ? (sortDirection==='asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (sortColumn === 'model') {
                      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortColumn('model')
                      setSortDirection('asc')
                    }
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Model{sortColumn==='model' ? (sortDirection==='asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (sortColumn === 'createdAt') {
                      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortColumn('createdAt')
                      setSortDirection('desc')
                    }
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  Upload Date{sortColumn==='createdAt' ? (sortDirection==='asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th
                  onClick={() => {
                    if (sortColumn === 'originalName') {
                      setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
                    } else {
                      setSortColumn('originalName')
                      setSortDirection('asc')
                    }
                  }}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  File Name{sortColumn==='originalName' ? (sortDirection==='asc' ? ' ▲' : ' ▼') : ''}
                </th>
                <th style={{ textAlign: 'center' }}>Download</th>
              </tr>
            </thead>

            {/* TABLE BODY */}
            <tbody>
              {filteredData.map((file) => (
                <tr 
                  key={file.id} 
                  className={`
                    ${file.id === activeRowId    ? styles.activeRow   : ''}
                    ${file.status !== 'Completed' ? styles.disabledRow : ''}
                  `}
                  onClick={() => file.status === 'Completed' && handleRowClick(file)}
                >
                  <td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.includes(file.id)}
                      onChange={() => handleCheckboxChange(file.id)}
                      disabled={file.status !== 'Completed'}
                    />
                  </td>
                  <td>{formatFileSize(file.fileSize)}</td>
                  <td>{getModelDisplay(file.model)}</td>
                  <td>{formatDate(file.createdAt || file.uploadDate)}</td>
                  <td>{file.originalName || file.fileName}</td>
                  <td style={{ position: 'relative', textAlign: 'center' }}>
                    <DownloadIcon
                      className={styles.downloadIcon}
                      onClick={e => handleMenuOpen(e, file.id)}
                    />

                    <Menu
                      anchorEl={anchorEl}
                      open={menuFileId === file.id}
                      onClose={handleMenuClose}
                      getContentAnchorEl={null}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                      <MenuItem
                        onClick={() => {
                          downloadFiles(
                            [file.id],
                            'csv',
                            () => (file.originalName || file.fileName || file.id).replace(/\.pdf$/i,''),
                            () => file.model,
                            false,
                            'full-csv'
                          )
                          handleMenuClose()
                        }}
                      >
                        Full CSV
                      </MenuItem>
                      {file.model === 'shipping-labels' ? (
                        <Tooltip title="Simple CSV download currently not supported for shipping labels"><span><MenuItem disabled>Simple CSV</MenuItem></span></Tooltip>
                      ) : (
                        <MenuItem
                          onClick={() => {
                            downloadFiles(
                              [file.id],
                              'csv',
                              () => (file.originalName || file.fileName || file.id).replace(/\.pdf$/i,''),
                              () => file.model,
                              false,
                              'simple-csv'
                            )
                            handleMenuClose()
                          }}
                        >
                          Simple CSV
                        </MenuItem>
                      )}
                      <MenuItem
                        onClick={() => {
                          downloadFiles(
                            [file.id],
                            'json',
                            () => (file.originalName || file.fileName || file.id).replace(/\.pdf$/i,'')
                          )
                          handleMenuClose()
                        }}
                      >
                        JSON
                      </MenuItem>
                    </Menu>
                  </td>
                </tr>
              ))}
            </tbody>

          </table>

          {error && <p style={{ color: 'red' }}>{error}</p>}

        </div>
      </section>
      
      {/* Paywall Modal - Shows for unsubscribed users */}
      {showPaywallModal && <PaywallModal />}
    </main>
  )
}

export default InventoryPage